/**
 * Prufe JMAP Client
 *
 * Handles all communication with the JMAP API.
 * Falls back gracefully when API is unavailable (offline mode).
 */

// In dev, the JMAP API is accessed via Lambda function URL.
// In production, this will be api.prufe.eu via API Gateway.
const API_BASE = import.meta.env.VITE_JMAP_API_URL || '';

interface JmapRequest {
  using: string[];
  methodCalls: [string, Record<string, unknown>, string][];
}

interface JmapResponse {
  methodResponses: [string, Record<string, unknown>, string][];
  sessionState: string;
}

class JmapClient {
  private apiUrl: string;
  private tenantId: string = '';
  private accountId: string = '';
  private available: boolean = false;
  private accessToken: string = '';

  constructor() {
    this.apiUrl = API_BASE ? `${API_BASE}/jmap` : '';
  }

  setContext(tenantId: string, accountId: string) {
    this.tenantId = tenantId;
    this.accountId = accountId;
  }

  setAccessToken(token: string) {
    this.accessToken = token;
  }

  isConfigured(): boolean {
    return this.apiUrl !== '' && this.tenantId !== '' && this.accountId !== '';
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    return headers;
  }

  async call(methodCalls: [string, Record<string, unknown>, string][]): Promise<JmapResponse> {
    if (!this.isConfigured()) {
      throw new Error('JMAP client not configured — set VITE_JMAP_API_URL and call setContext()');
    }

    const request: JmapRequest = {
      using: [
        'urn:ietf:params:jmap:core',
        'urn:ietf:params:jmap:mail',
        'urn:ietf:params:jmap:contacts',
        'urn:ietf:params:jmap:calendars',
      ],
      methodCalls,
    };

    // When authenticated via API Gateway, JWT carries tenant/account context.
    // Fall back to query params for local dev without auth.
    const params = this.accessToken
      ? ''
      : `?tenantId=${encodeURIComponent(this.tenantId)}&accountId=${encodeURIComponent(this.accountId)}`;

    const url = `${this.apiUrl}${params}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`JMAP request failed: ${response.status} ${response.statusText}`);
    }

    this.available = true;
    return response.json();
  }

  async invoke(method: string, args: Record<string, unknown> = {}): Promise<Record<string, unknown>> {
    const resp = await this.call([[method, { accountId: this.accountId, ...args }, 'c0']]);
    if (resp.methodResponses.length === 0) {
      throw new Error('Empty JMAP response');
    }
    const [, result] = resp.methodResponses[0];
    return result;
  }

  // --- Email ---
  async getMailboxes() { return this.invoke('Mailbox/get'); }
  async queryEmails(filter: Record<string, unknown> = {}, limit = 50, position = 0) {
    return this.invoke('Email/query', { filter, limit, position });
  }

  /**
   * Search emails with snippets — batches Email/query + Email/get + SearchSnippet/get
   * into a single JMAP request using result references.
   */
  async searchEmails(filter: Record<string, unknown>, limit = 50, position = 0, sort?: string): Promise<{
    ids: string[];
    total: number;
    emails: Record<string, unknown>[];
    snippets: { emailId: string; subject: string | null; preview: string | null; source: string | null }[];
  }> {
    const resp = await this.call([
      ['Email/query', { accountId: this.accountId, filter, limit, position, ...(sort ? { sort } : {}) }, 'q'],
      ['Email/get', {
        accountId: this.accountId,
        '#ids': { resultOf: 'q', name: 'Email/query', path: '/ids' },
      }, 'g'],
      ['SearchSnippet/get', {
        accountId: this.accountId,
        '#emailIds': { resultOf: 'q', name: 'Email/query', path: '/ids' },
        filter,
      }, 's'],
    ]);

    const queryResult = resp.methodResponses.find(r => r[2] === 'q')?.[1] || {};
    const getResult = resp.methodResponses.find(r => r[2] === 'g')?.[1] || {};
    const snippetResult = resp.methodResponses.find(r => r[2] === 's')?.[1] || {};

    return {
      ids: (queryResult.ids as string[]) || [],
      total: (queryResult.total as number) || 0,
      emails: (getResult.list as Record<string, unknown>[]) || [],
      snippets: (snippetResult.list as { emailId: string; subject: string | null; preview: string | null; source: string | null }[]) || [],
    };
  }

  async getEmails(ids: string[], properties?: string[]) {
    const args: Record<string, unknown> = { ids };
    if (properties) args.properties = properties;
    return this.invoke('Email/get', args);
  }
  async updateEmail(emailId: string, patch: Record<string, unknown>) {
    return this.invoke('Email/set', { update: { [emailId]: patch } });
  }
  async deleteEmail(emailId: string) {
    return this.invoke('Email/set', { destroy: [emailId] });
  }
  /**
   * Move emails into `targetMailboxId`, removing them from `fromMailboxId` when given.
   * Used by "Move to…" and by "Restore" out of Trash (the backend also propagates a
   * restore back to the source for synced external mail).
   */
  async moveEmails(emailIds: string[], targetMailboxId: string, fromMailboxId?: string | null) {
    const update: Record<string, Record<string, unknown>> = {};
    for (const id of emailIds) {
      const patch: Record<string, unknown> = { [`mailboxIds/${targetMailboxId}`]: true };
      if (fromMailboxId && fromMailboxId !== targetMailboxId) {
        patch[`mailboxIds/${fromMailboxId}`] = null;
      }
      update[id] = patch;
    }
    return this.invoke('Email/set', { update });
  }
  async createMailbox(name: string, color?: string, parentId?: string) {
    const props: Record<string, unknown> = { name };
    if (color) props.color = color;
    if (parentId) props.parentId = parentId;
    return this.invoke('Mailbox/set', { create: { new: props } });
  }

  // --- Contacts ---
  async getContacts() { return this.invoke('Contact/get'); }
  async createContact(props: Record<string, unknown>) {
    return this.invoke('Contact/set', { create: { new: props } });
  }
  async updateContact(contactId: string, patch: Record<string, unknown>) {
    return this.invoke('Contact/set', { update: { [contactId]: patch } });
  }
  async deleteContact(contactId: string) {
    return this.invoke('Contact/set', { destroy: [contactId] });
  }
  async getContactGroups() { return this.invoke('ContactGroup/get'); }

  // --- Calendar ---
  // --- Calendar ---
  async getCalendars() { return this.invoke('Calendar/get'); }
  async createCalendar(props: { name: string; color?: string; isVisible?: boolean; isDefault?: boolean }) {
    return this.invoke('Calendar/set', { create: { new: props } });
  }
  async updateCalendar(calendarId: string, patch: Record<string, unknown>) {
    return this.invoke('Calendar/set', { update: { [calendarId]: patch } });
  }
  async deleteCalendar(calendarId: string) {
    return this.invoke('Calendar/set', { destroy: [calendarId] });
  }

  /** All stored events (masters, not expanded). */
  async getCalendarEvents(ids?: string[]) {
    return this.invoke('CalendarEvent/get', ids ? { ids } : {});
  }
  /** Events overlapping [after, before], with recurrences expanded into instances. */
  async queryCalendarEvents(after: string, before: string, calendarId?: string) {
    const filter: Record<string, unknown> = { after, before };
    if (calendarId) filter.calendarId = calendarId;
    return this.invoke('CalendarEvent/query', { filter });
  }
  async createCalendarEvent(props: Record<string, unknown>) {
    return this.invoke('CalendarEvent/set', { create: { new: props } });
  }
  async updateCalendarEvent(eventId: string, patch: Record<string, unknown>) {
    return this.invoke('CalendarEvent/set', { update: { [eventId]: patch } });
  }
  async deleteCalendarEvent(eventId: string) {
    return this.invoke('CalendarEvent/set', { destroy: [eventId] });
  }
  /** Delete one occurrence of a recurring series (adds an EXDATE to the master). */
  async excludeOccurrence(masterId: string, recurrenceId: string) {
    return this.invoke('CalendarEvent/set', { update: { [masterId]: { addExdate: recurrenceId } } });
  }

  // --- Saved Searches (Smart Folders) ---
  async getSavedSearches() { return this.invoke('SavedSearch/get'); }
  async createSavedSearch(props: { name: string; query: string; filter?: Record<string, unknown>; icon?: string; sortOrder?: number }) {
    return this.invoke('SavedSearch/set', { create: { new: props } });
  }
  async updateSavedSearch(searchId: string, patch: Record<string, unknown>) {
    return this.invoke('SavedSearch/set', { update: { [searchId]: patch } });
  }
  async deleteSavedSearch(searchId: string) {
    return this.invoke('SavedSearch/set', { destroy: [searchId] });
  }

  // --- Identity ---
  async getIdentities() { return this.invoke('Identity/get'); }

  // --- Session ---
  async getSession() {
    if (!API_BASE) throw new Error('API not configured');
    const params = this.accessToken
      ? ''
      : `?accountId=${encodeURIComponent(this.accountId)}`;
    const url = `${API_BASE}/.well-known/jmap${params}`;
    const response = await fetch(url, { headers: this.getHeaders() });
    if (!response.ok) throw new Error(`Session request failed: ${response.status}`);
    return response.json();
  }
}

export const jmapClient = new JmapClient();
